import { ErrorPane } from '@criteria-labs/react'
import { useEffect, useState, useSyncExternalStore } from 'react'
import { Document, useDocumentError, useDocumentType } from '../documents/Document'
import { DocumentStore } from '../documents/DocumentStore'
import { JSONDocument } from '../documents/JSONDocument'
import { JSONSchemaDocument } from '../documents/JSONSchemaDocument'
import { OpenAPIDocument } from '../documents/OpenAPIDocument'
import { SpectralRulesetDocument } from '../documents/SpectralRulesetDocument'
import { SourceRange } from '../issues/Issue'
import { DocumentPreview } from './DocumentPreview'
import { DocumentTextEditor } from './DocumentTextEditor'
import { SplitLayout } from './SplitLayout'
import { OpenAPIDocumentInspector } from './OpenAPIDocumentInspector'

export function DocumentView({
  document,
  textSelection,
  mode
}: {
  document: Document
  textSelection?: SourceRange
  mode: 'code' | 'inspector'
}) {
  const [preferredLocation, setPreferredLocation] = useState<string>('#')

  const documentType = useDocumentType(document)
  const documentError = useDocumentError(document)

  // TOOD: move to document store so don't have to open the file
  const proposedDocumentType = useSyncExternalStore(
    (onStoreChange) => {
      document.addListener(onStoreChange)
      return () => document.removeListener(onStoreChange)
    },
    () => {
      if (document instanceof OpenAPIDocument) {
        return 'OpenAPIDocument'
      }
      if (document instanceof JSONSchemaDocument) {
        return 'JSONSchemaDocument'
      }
      if (document instanceof SpectralRulesetDocument) {
        return 'SpectralRulesetDocument'
      }
      if (document instanceof JSONDocument) {
        if (document.data) {
          if (OpenAPIDocument.isOpenAPIData(document.data)) {
            return 'OpenAPIDocument'
          }
          if (JSONSchemaDocument.isOpenAPIData(document.data)) {
            return 'JSONSchemaDocument'
          }
          if (SpectralRulesetDocument.isSpectralRulesetData(document.data)) {
            return 'SpectralRulesetDocument'
          }
        } else {
          document.reloadContent().catch(console.error)
        }
      }
      return null
    }
  )

  useEffect(() => {
    if (proposedDocumentType) {
      DocumentStore.shared().setDocumentType(document, proposedDocumentType)
    }
  }, [document, proposedDocumentType])

  if (mode === 'inspector' && document instanceof OpenAPIDocument) {
    return <OpenAPIDocumentInspector key={documentType} document={document} />
  }

  return (
    <SplitLayout
      firstContent={
        <DocumentTextEditor
          document={document}
          preferredLocation={preferredLocation}
          setPreferredLocation={setPreferredLocation}
          textSelection={textSelection}
        />
      }
      secondContent={
        documentError ? <ErrorPane error={documentError} /> : <DocumentPreview key={documentType} document={document} />
      }
    />
  )
}
